import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import type { Owner } from '../../../@types/Owner';
import { OWNER_STEPS } from '../../../@types/Owner';
import { fetchCopro } from '../../../store/CoproReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { getCompanyTeamRentals, getCompanyTeamTransactions, getCompanyTeamWeeklyPlanning } from '../../../utils/api';
import { getNextScheduledMeeting } from '../../../utils/contactUtils';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Popup from '../../common/Popup/Popup.module';
import EmailConfirmDialog from './components/EmailConfirmDialog';
import EmailTemplatesPreview from './components/EmailTemplatesPreview';
import Header from './components/Header';
import MobileNav from './components/MobileNav';
import NewAppointmentForm from './components/NewAppointmentForm';
import ScheduledAppointments from './components/ScheduledAppointments';
import SidebarMenu from './components/SidebarMenu';
import TeamGrid from './components/TeamGrid';
import Tracking from './components/Tracking';
import WeeklyPlanning from './components/WeeklyPlanning';
import type { MeetingWithUser, MenuSection, RdvItem, SectionId, TeamMember, TeamStatsApiUser, WeeklyPlanningApiRow, WeeklyPlanningItem } from './types';

 

function ReferentAppointments() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [rdvs, setRdvs] = useState<RdvItem[]>([]);
  const [scheduledBannerMessage, setScheduledBannerMessage] = useState<string | null>(null);
  const [scheduledHighlightId, setScheduledHighlightId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRdv, setEditingRdv] = useState<RdvItem | null>(null);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailConfirmRdv, setEmailConfirmRdv] = useState<RdvItem | null>(null);
  const [emailConfirmSubmitting, setEmailConfirmSubmitting] = useState(false);
  

  

  const [activeSection, setActiveSection] = useState<SectionId>('nouveau-rdv');
  const [menuSections, setMenuSections] = useState<MenuSection[]>([
    {
      id: 'actions',
      title: 'ACTIONS',
      emoji: `<span role="img" aria-label="telephone">📞</span>`,
      icon: '▼',
      expanded: true,
      items: [
        { id: 'nouveau-rdv', title: 'Nouveau Rendez-vous', emoji: `<span role="img" aria-label="telephone">📞</span>`, icon: '' },
        { id: 'rdv-programmes', title: 'RDV Programmés', emoji: `<span role="img" aria-label="calendar">📅</span>`, icon: '' },
        { id: 'suivi-rdv', title: 'Suivi & Contrôle', emoji: `<span role="img" aria-label="chart">📊</span>`, icon: '' },
      ],
    },
    {
      id: 'emails',
      title: 'MODÈLES MAILS',
      emoji: `<span role="img" aria-label="email">📧</span>`,
      icon: '▼',
      expanded: false,
      items: [
        { id: 'modele-client', title: 'Confirmation Client', emoji: `<span role="img" aria-label="person">👤</span>`, icon: '' },
        { id: 'modele-collaborateur', title: 'Affectation Collaborateur', emoji: `<span role="img" aria-label="people">👥</span>`, icon: '' },
        { id: 'modele-relance', title: 'Relance Collaborateur', emoji: `<span role="img" aria-label="loudspeaker">📢</span>`, icon: '' },
      ],
    },
    {
      id: 'team',
      title: 'GESTION D\'ÉQUIPE',
      emoji: `<span role="img" aria-label="people">👥</span>`,
      icon: '▼',
      expanded: false,
      items: [
        { id: 'equipe-vente', title: 'Équipe Transaction', emoji: `<span role="img" aria-label="house">🏠</span>`, icon: '' },
        { id: 'equipe-location', title: 'Équipe Location', emoji: `<span role="img" aria-label="key">🔑</span>`, icon: '' },
        { id: 'planning-equipes', title: 'Planning & Disponibilités', emoji: `<span role="img" aria-label="chart">📊</span>`, icon: '' },
      ],
    },
  ]);

  const sectionToRoute: Record<SectionId, string> = {
    'nouveau-rdv': ROUTES.REFERENT_APPOINTMENTS_NEW,
    'rdv-programmes': ROUTES.REFERENT_APPOINTMENTS_SCHEDULED,
    'suivi-rdv': ROUTES.REFERENT_APPOINTMENTS_TRACKING,
    'modele-client': ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_CLIENT,
    'modele-collaborateur': ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_COLLAB,
    'modele-relance': ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_RELANCE,
    'equipe-vente': ROUTES.REFERENT_APPOINTMENTS_TEAM_SALES,
    'equipe-location': ROUTES.REFERENT_APPOINTMENTS_TEAM_RENTAL,
    'planning-equipes': ROUTES.REFERENT_APPOINTMENTS_TEAM_PLANNING,
  };
  const routeToSection = (pathname: string): SectionId | null => {
    switch (pathname) {
      case ROUTES.REFERENT_APPOINTMENTS_NEW: return 'nouveau-rdv';
      case ROUTES.REFERENT_APPOINTMENTS_SCHEDULED: return 'rdv-programmes';
      case ROUTES.REFERENT_APPOINTMENTS_TRACKING: return 'suivi-rdv';
      case ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_CLIENT: return 'modele-client';
      case ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_COLLAB: return 'modele-collaborateur';
      case ROUTES.REFERENT_APPOINTMENTS_TEMPLATE_RELANCE: return 'modele-relance';
      case ROUTES.REFERENT_APPOINTMENTS_TEAM_SALES: return 'equipe-vente';
      case ROUTES.REFERENT_APPOINTMENTS_TEAM_RENTAL: return 'equipe-location';
      case ROUTES.REFERENT_APPOINTMENTS_TEAM_PLANNING: return 'planning-equipes';
      default: return null;
    }
  };

  const [transactionTeam, setTransactionTeam] = useState<TeamMember[]>([]);
  const [rentalTeam, setRentalTeam] = useState<TeamMember[]>([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [errorTransactions, setErrorTransactions] = useState<string | null>(null);
  const [errorRentals, setErrorRentals] = useState<string | null>(null);
  const [weeklyPlanning, setWeeklyPlanning] = useState<WeeklyPlanningItem[]>([]);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  const [errorPlanning, setErrorPlanning] = useState<string | null>(null);
  const [selectedWeekDate, setSelectedWeekDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  });
  const auth = useSelector((s: RootState) => s.auth);
  const copros = useSelector((s: RootState) => s.copro.copros);
  const company = useSelector((s: RootState) => s.auth.company);
  const selectedCoproId = copros?.[0]?.id as number | undefined;

  // Ensure copros list is loaded so we can pick a coproId
  useEffect(() => {
    if (copros?.length === 0) {
      dispatch(fetchCopro(null));
    }
  }, [copros?.length, dispatch]);

  // Sync URL -> section on navigation and initial mount
  useEffect(() => {
    const current = routeToSection(location.pathname);
    if (current) {
      if (current !== activeSection) {
        setActiveSection(current);
        setMenuSections(prev => prev.map(section => {
          const hasItem = section.items.some(item => item.id === current);
          if (hasItem) { return { ...section, expanded: true }; }
          return section;
        }));
      }
      return;
    }
    if (location.pathname === ROUTES.REFERENT_APPOINTMENTS) {
      // Normalize base path to default section
      navigate(ROUTES.REFERENT_APPOINTMENTS_NEW, { replace: true });
    }
  }, [location.pathname]);

  // Fetch when team sections are displayed or when coproId becomes available
  useEffect(() => {
    const loadTransactions = async(): Promise<void> => {
      if (!auth.token || !company?.id) { return; }
      setLoadingTransactions(true); setErrorTransactions(null);
      const headers = JWTHeader(auth.token);
      try {
        const res = await fetch(getCompanyTeamTransactions(company.id), headers);
        if (!res.ok) { throw new Error(''); }
        const arr = await res.json();
        setTransactionTeam((arr as TeamStatsApiUser[]).map((u: TeamStatsApiUser) => ({
          id: String(u.id),
          nom: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim(),
          role: 'Transactionnaire',
          initiales: `${(u.firstname?.[0] ?? '').toUpperCase()}${(u.lastname?.[0] ?? '').toUpperCase()}`,
          rdvSemaineEnCours: u.rdvThisWeek ?? 0,
          rdvSemaineAVenir: u.rdvNextWeek ?? 0,
          tauxReussite: Math.round(u.conversionRate ?? 0),
          disponible: true,
          status: 'available',
          type: 'vente',
          email: (u.email ?? u.user?.email ?? '').toString() || undefined,
        })) as TeamMember[]);
      } catch {
        setErrorTransactions('Une erreur est survenue lors du chargement.');
        setTransactionTeam([]);
      } finally {
        setLoadingTransactions(false);
      }
    };

    const loadRentals = async(): Promise<void> => {
      if (!auth.token || !company?.id) { return; }
      setLoadingRentals(true); setErrorRentals(null);
      const headers = JWTHeader(auth.token);
      try {
        const res = await fetch(getCompanyTeamRentals(company.id), headers);
        if (!res.ok) { throw new Error(''); }
        const arr = await res.json();
        setRentalTeam((arr as TeamStatsApiUser[]).map((u: TeamStatsApiUser) => ({
          id: String(u.id),
          nom: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim(),
          role: 'Gestionnaire Locatif',
          initiales: `${(u.firstname?.[0] ?? '').toUpperCase()}${(u.lastname?.[0] ?? '').toUpperCase()}`,
          rdvSemaineEnCours: u.rdvThisWeek ?? 0,
          rdvSemaineAVenir: u.rdvNextWeek ?? 0,
          tauxReussite: Math.round(u.conversionRate ?? 0),
          disponible: true,
          status: 'available',
          type: 'location',
          email: (u.email ?? u.user?.email ?? '').toString() || undefined,
        })) as TeamMember[]);
      } catch {
        setErrorRentals('Une erreur est survenue lors du chargement.');
        setRentalTeam([]);
      } finally {
        setLoadingRentals(false);
      }
    };

    if (activeSection === 'equipe-vente') { loadTransactions(); }
    if (activeSection === 'equipe-location') { loadRentals(); }
    if (activeSection === 'planning-equipes') {
      const loadPlanning = async(): Promise<void> => {
        if (!auth.token || !company?.id) { return; }
        setLoadingPlanning(true); setErrorPlanning(null);
        const headers = JWTHeader(auth.token);
        try {
          const res = await fetch(getCompanyTeamWeeklyPlanning(company.id, selectedWeekDate), headers);
          if (!res.ok) { throw new Error(''); }
          const arr = await res.json();
          const typed = (arr as WeeklyPlanningApiRow[]).map((row: WeeklyPlanningApiRow) => ({
            id: Number(row.id),
            firstname: (row.firstname ?? null) as string | null,
            lastname: (row.lastname ?? null) as string | null,
            type: (row.type ?? 'vente') as 'vente' | 'location',
            days: {
              mon: Number(row.days?.mon ?? 0),
              tue: Number(row.days?.tue ?? 0),
              wed: Number(row.days?.wed ?? 0),
              thu: Number(row.days?.thu ?? 0),
              fri: Number(row.days?.fri ?? 0),
            },
            weeklyTotal: Number(row.weeklyTotal ?? 0),
            weeklyCapacity: Number(row.weeklyCapacity ?? 8),
          })) as WeeklyPlanningItem[];
          setWeeklyPlanning(typed);
        } catch {
          setErrorPlanning('Une erreur est survenue lors du chargement.');
          setWeeklyPlanning([]);
        } finally {
          setLoadingPlanning(false);
        }
      };
      loadPlanning();
    }
  }, [activeSection, auth.token, selectedCoproId, selectedWeekDate, dispatch]);

  // Fetch scheduled appointments from backend when the section is opened
  useEffect(() => {
    const loadScheduled = async(): Promise<void> => {
      if (activeSection !== 'rdv-programmes') { return; }
      if (!auth.token || !company?.id) { return; }
      setLoadingScheduled(true);
      try {
        const headers = JWTHeader(auth.token);
        const steps: Array<{ step: string; sortField: string }> = [
          { step: OWNER_STEPS.WAITING_FOR_MEETING_FROM_INCOMING_CALL_SALES_MANAGER, sortField: 'salesManagerInteraction.meetingAt' },
          { step: OWNER_STEPS.WAITING_FOR_MEETING_FROM_INCOMING_CALL_RENT_MANAGER, sortField: 'rentManagerInteraction.meetingAt' },
        ];
        const requests = steps.map(({ step, sortField }) => {
          const url = `/api/companies/${company.id}/owners?filterStep=${encodeURIComponent(step)}&limit=100&sortField=${encodeURIComponent(sortField)}&sortDirection=asc`;
          return fetch(url, headers);
        });
        const responses = await Promise.all(requests);
        const jsons = await Promise.all(responses.map(async(res, idx) => {
          if (!res.ok) {
            console.warn('[RDV] response not ok', { idx, status: res.status });
            return null;
          }
          try {
            const data = await res.json();
            return data;
          } catch (e) {
            console.error('[RDV] json parse failed', { idx, error: (e as Error).message });
            return null;
          }
        }));
        const ownersArrays: Owner[] = [];
        jsons.forEach((data) => {
          if (!data) { return; }
          const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
          ownersArrays.push(...results);
        });
        // Do not filter by copro here to avoid hiding valid scheduled meetings across the company scope
        const filteredOwners = ownersArrays;
        const mapped: RdvItem[] = filteredOwners.map((owner: Owner): RdvItem | null => {
          const meeting = getNextScheduledMeeting(owner);
          if (!meeting?.meetingAt) { return null; }
          const raw = String(meeting.meetingAt);
          const m = raw.match(/^(\d{4}-\d{2}-\d{2})[T\s](\d{2}:\d{2})/);
          let date = '';
          let heure = '';
          if (m) {
            date = m[1];
            heure = m[2];
          } else {
            // Fallback: best-effort without timezone shifts
            try {
              const dt = new Date(raw);
              date = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
              heure = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
            } catch {
              return null;
            }
          }
          const demande: 'vente' | 'location' = owner?.step?.includes('rent') ? 'location' : 'vente';
          const firstname = (owner?.firstname ?? '').toString();
          const lastname = (owner?.lastname ?? '').toString();
          const phone = (owner?.phone ?? '').toString();
          const email = (owner?.email ?? '').toString();
          const address = (
            owner?.address ??
            owner?.dpe?.copro?.address ??
            (owner?.incomingCallCopro?.addresses?.[0]?.address as string | undefined) ??
            ''
          ) as string;
          const meetingWith = meeting?.meetingWith as MeetingWithUser | undefined;
          const assigneA = {
            id: String(meetingWith?.id ?? ''),
            nom: `${(meetingWith?.firstname ?? '')} ${(meetingWith?.lastname ?? '')}`.trim() || (meetingWith?.name ?? '—'),
            role: demande === 'vente' ? 'Transactionnaire' : 'Gestionnaire Locatif',
            initiales: `${(meetingWith?.firstname?.[0] ?? '')}${(meetingWith?.lastname?.[0] ?? '')}`.toUpperCase() || '—',
            rdvSemaineEnCours: 0,
            rdvSemaineAVenir: 0,
            tauxReussite: 0,
            disponible: true,
            status: 'available',
            type: demande,
            email: (meetingWith?.email ?? '').toString() || undefined,
          } as TeamMember;
          return {
            id: `owner-${owner.id}`,
            date,
            heure,
            contact: { civilite: '', nom: `${firstname} ${lastname}`.trim(), tel: phone, email },
            bien: { type: owner?.dpe?.details?.type || owner?.dpe?.details?.typeBien || '—', adresse: address },
            demande,
            assigneA,
            notes: (owner?.qualityReferenceInteractions?.[0]?.comment ?? owner?.rentManagerInteractions?.[0]?.comment ?? owner?.salesManagerInteractions?.[0]?.comment ?? undefined) as string | undefined,
            statut: 'programme',
            emailSent: false,
            emailPending: false,
            emailSentDate: null,
          } as RdvItem;
        }).filter(Boolean) as RdvItem[];

        // Replace state with only incoming-call items, preserving any locally-created entries (ids starting with 'rdv-')
        setRdvs(prev => {
          const signature = (r: RdvItem) => `${r.date}|${r.heure}|${(r.contact.tel || '').replaceAll(/\s+/g, '')}`;
          const local = prev.filter(r => r.id.startsWith('rdv-'));
          const existing = new Set(local.map(signature));
          const merged = [...local];
          mapped.forEach(r => { if (!existing.has(signature(r))) { merged.push(r); existing.add(signature(r)); } });
          return merged;
        });
      } catch {
        // silent fail — keep local state
      } finally {
        setLoadingScheduled(false);
      }
    };
    loadScheduled();
  }, [activeSection, auth.token, company?.id, selectedCoproId]);

  const handleBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch {
      navigate(ROUTES.HOME);
    }
  };

  const handleCreateRdv = (rdv: RdvItem) => {
    setRdvs(prev => [rdv, ...prev]);
    // Redirect UX: switch to "RDV Programmés" with success banner
    navigate(sectionToRoute['rdv-programmes']);
    try {
      const dateLabel = new Date(rdv.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      setScheduledBannerMessage(`${dateLabel} à ${rdv.heure} — ${rdv.contact.civilite} ${rdv.contact.nom}`);
    } catch {
      setScheduledBannerMessage(`RDV programmé — ${rdv.contact.civilite} ${rdv.contact.nom}`);
    }
    setScheduledHighlightId(rdv.id);
  };

  const handleUpdateRdv = (id: string, patch: Partial<RdvItem>) => {
    // Intercept "Confirmer" click from list to open confirmation popup instead of immediately mutating state
    if (patch.emailPending === true && Object.keys(patch).length === 1) {
      const target = rdvs.find(r => r.id === id) || null;
      setEmailConfirmRdv(target);
      setEmailConfirmOpen(Boolean(target));
      return;
    }
    setRdvs(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const handleDeleteRdv = (id: string) => {
    setRdvs(prev => prev.filter(r => r.id !== id));
  };

  const handleEditRequested = (rdv: RdvItem) => {
    setEditingRdv(rdv);
    setEditModalOpen(true);
  };

  const handleSaveEditedRdv = (id: string, updated: Partial<RdvItem>) => {
    setRdvs(prev => prev.map(r => (r.id === id ? { ...r, ...updated } : r)));
    setEditModalOpen(false);
    setEditingRdv(null);
  };

  const toggleMenuSection = (sectionId: string) => {
    setMenuSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        // Keep 'actions' always visible/non-collapsible
        if (section.id === 'actions') { return section; }
        return { ...section, expanded: !section.expanded };
      }
      // Do not auto-collapse other sections
      return section;
    }));
  };

  const showContent = (contentId: SectionId) => {
    setActiveSection(contentId);
    setMenuSections(prev => prev.map(section => {
      const hasItem = section.items.some(item => item.id === contentId);
      if (hasItem) {
        return { ...section, expanded: true };
      }
      return section;
    }));
    navigate(sectionToRoute[contentId]);
  };

  

  const formatDateFR = (dateStr: string): string => {
    const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) {
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      } catch { return dateStr; }
    }
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(year, month - 1, day, 12, 0, 0);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const renderEmailPreviewForRdv = (rdv: RdvItem, type: 'client' | 'collaborateur') => {
    const dateFormatted = formatDateFR(rdv.date);
    if (type === 'client') {
      return (
        <div className="rounded-xl border-2 border-slate-200 bg-white p-4 text-sm">
          <div className="-mx-4 -mt-4 rounded-t-xl bg-rgpd-blue px-4 py-2 text-center font-bold text-white">Email Client</div>
          <div className="mt-3 space-y-2 text-slate-700">
            <p>Bonjour <strong>{rdv.contact.civilite} {rdv.contact.nom}</strong>,</p>
            <p>Nous vous confirmons votre rendez-vous d'estimation :</p>
            <div className="rounded border-l-4 border-rgpd-blue bg-blue-50 p-3">
              <div><strong><span role="img" aria-label="calendar">📅</span> Date :</strong> {dateFormatted}</div>
              <div><strong><span role="img" aria-label="clock">⏰</span> Heure :</strong> {rdv.heure}</div>
              <div><strong><span role="img" aria-label="location">📍</span> Adresse :</strong> {rdv.bien.adresse}</div>
              <div><strong><span role="img" aria-label="person">👤</span> Expert :</strong> {rdv.assigneA.nom}</div>
            </div>
            <p>Cordialement,<br/>L'équipe Agence</p>
          </div>
        </div>
      );
    }
    const collabFirstname = (rdv.assigneA.nom || '').split(' ')[0] || '';
    return (
      <div className="rounded-xl border-2 border-slate-200 bg-white p-4 text-sm">
        <div className="-mx-4 -mt-4 rounded-t-xl bg-emerald-600 px-4 py-2 text-center font-bold text-white">Email Collaborateur</div>
        <div className="mt-3 space-y-2 text-slate-700">
          <p>Bonjour <strong>{collabFirstname}</strong>,</p>
          <p>Un nouveau RDV vous a été assigné :</p>
          <div className="rounded border-l-4 border-emerald-600 bg-emerald-50 p-3">
            <div><strong><span role="img" aria-label="calendar">📅</span> Date :</strong> {dateFormatted}</div>
            <div><strong><span role="img" aria-label="clock">⏰</span> Heure :</strong> {rdv.heure}</div>
            <div><strong><span role="img" aria-label="person">👤</span> Client :</strong> {rdv.contact.civilite} {rdv.contact.nom}</div>
            <div><strong><span role="img" aria-label="location">📍</span> Adresse :</strong> {rdv.bien.adresse}</div>
          </div>
          <p>Merci de confirmer la prise en compte.</p>
          <p>Le service référent</p>
        </div>
      </div>
    );
  };

  const confirmEmailSending = () => {
    if (!emailConfirmRdv) { return; }
    const id = emailConfirmRdv.id;
    setEmailConfirmSubmitting(true);
    try { window.alert('Fonctionnalité en developpement, veuillez envoyer les mails'); } catch {}
    // Mark as pending to reflect progress in the list
    setRdvs(prev => prev.map(r => (r.id === id ? { ...r, emailPending: true } : r)));
    // Simulate API call
    setTimeout(() => {
      try {
        setRdvs(prev => prev.map(r => (r.id === id ? { ...r, emailPending: false, emailSent: true, emailSentDate: new Date().toISOString() } : r)));
        setEmailConfirmOpen(false);
        setEmailConfirmRdv(null);
      } finally {
        setEmailConfirmSubmitting(false);
      }
    }, 1500);
  };

  const copyHtmlFromRef = async(ref: React.RefObject<HTMLDivElement>): Promise<void> => {
    const container = ref.current;
    if (!container) { return; }
    const html = container.innerHTML;
    const text = container.innerText;
    try {
      // Modern API: write HTML + Plain
      interface ClipboardWindow {
        ClipboardItem?: new (items: Record<string, Blob>) => ClipboardItem;
      }
      interface ClipboardWithWrite {
        write?(data: ClipboardItem[]): Promise<void>;
      }
      const canWriteRich = (window as unknown as ClipboardWindow).ClipboardItem !== undefined && navigator.clipboard && (navigator.clipboard as unknown as ClipboardWithWrite).write;
      if (canWriteRich) {
        const ClipboardItemCtor = (window as unknown as ClipboardWindow).ClipboardItem!;
        const data = [
          new ClipboardItemCtor({ 'text/html': new Blob([html], { type: 'text/html' }) }),
          new ClipboardItemCtor({ 'text/plain': new Blob([text], { type: 'text/plain' }) }),
        ];
        await (navigator.clipboard as unknown as ClipboardWithWrite).write!(data);
        return;
      }
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback: create a temporary element and select
      try {
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      } catch {}
    }
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'nouveau-rdv':
        return (
          <NewAppointmentForm
            transactionTeam={transactionTeam}
            rentalTeam={rentalTeam}
            onCreateRdv={handleCreateRdv}
            coproId={selectedCoproId as number}
          />
        );
      case 'rdv-programmes':
        return (
          <ScheduledAppointments
            items={rdvs}
            loading={loadingScheduled}
            onUpdate={handleUpdateRdv}
            onDelete={handleDeleteRdv}
            onEdit={handleEditRequested}
            bannerMessage={scheduledBannerMessage}
            onDismissBanner={() => setScheduledBannerMessage(null)}
            highlightId={scheduledHighlightId ?? undefined}
          />
        );
      case 'suivi-rdv':
        return (<Tracking />);
      case 'modele-client':
        return (<EmailTemplatesPreview kind="client" />);
      case 'modele-collaborateur':
        return (<EmailTemplatesPreview kind="collab" />);
      case 'modele-relance':
        return (<EmailTemplatesPreview kind="relance" />);
      case 'equipe-vente':
        return (<TeamGrid team={transactionTeam} loading={loadingTransactions} error={errorTransactions} emptyLabel="l’équipe Transaction" />);
      case 'equipe-location':
        return (<TeamGrid team={rentalTeam} loading={loadingRentals} error={errorRentals} emptyLabel="l’équipe Location" />);
      case 'planning-equipes':
        return (
          <WeeklyPlanning
            selectedWeekDate={selectedWeekDate}
            onChangeWeekDate={setSelectedWeekDate}
            loading={loadingPlanning}
            error={errorPlanning}
            rows={weeklyPlanning}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto">
      <div className="rounded-lg bg-white p-8 shadow-md">
        <div className="mb-3 flex items-center justify-between">
          <button onClick={handleBack} className="text-rgpd-blue transition-colors hover:text-rgpd-blue-dark">← Retour</button>
        </div>

        <Header />

        {/* Mobile navigation (tabs + select) */}
        <MobileNav
          menuSections={menuSections}
          activeSection={activeSection}
          onSelectSection={showContent}
        />

        <div className="flex min-h-[60vh]">
          {/* Sidebar Menu */}
          <SidebarMenu
            menuSections={menuSections}
            activeSection={activeSection}
            onToggleSection={toggleMenuSection}
            onSelectSection={showContent}
          />

          {/* Main Content Area */}
          <div className="flex-1 space-y-6 p-1">
            {renderContent()}
            <Popup
              isOpen={editModalOpen}
              onClose={() => { setEditModalOpen(false); setEditingRdv(null); }}
              title={<div className="flex items-center gap-2"><span role="img" aria-label="pencil">✏️</span> Modifier le Rendez-vous</div>}
              additionalClass="w-full max-w-3xl"
            >
              {editingRdv && (
                <NewAppointmentForm
                  transactionTeam={transactionTeam}
                  rentalTeam={rentalTeam}
                  mode="edit"
                  initialRdv={editingRdv}
                  onUpdateRdv={handleSaveEditedRdv}
                />
              )}
            </Popup>

            <EmailConfirmDialog
              isOpen={emailConfirmOpen}
              submitting={emailConfirmSubmitting}
              rdv={emailConfirmRdv}
              onClose={() => { if (!emailConfirmSubmitting) { setEmailConfirmOpen(false); setEmailConfirmRdv(null); } }}
              onConfirm={confirmEmailSending}
              renderEmailPreviewForRdv={renderEmailPreviewForRdv}
              onCopyHtml={copyHtmlFromRef}
            />
                  </div>
        </div>
      </div>
    </div>
  );
}

export default ReferentAppointments;


